import React from 'react';
import { Box } from '@mui/material';
import { type Control, type FieldValues, type Path } from 'react-hook-form';

import AutocompleteInputBase from '/packages/shared-components/forms/AutocompleteInput/AutocompleteInputBase';

import { SkuOptionRow } from './SkuOptionRow';
import {
  filterSkuOptions,
  getSkuDisplayLabel,
  normalizeSkuFieldValue,
  toInputString,
  type SkuOption,
} from './skuSelectInputUtils';

export type { SkuOption };

export interface SkuSelectInputProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  options: SkuOption[];
  disabled?: boolean;
  freeSolo?: boolean;
  loading?: boolean;
  required?: boolean;
  hideLabel?: boolean;
  placeholder?: string;
  dataCy?: string;
  hideErrorMsg?: boolean;
}

const renderSkuOption = (_: React.HTMLAttributes<HTMLLIElement>, option: SkuOption) => (
  <Box
    key={`sku-option-${option.value}`}
    data-cy={`sku-option-${option.key}`}
    style={{
      height: 'auto',
      minHeight: '50px',
      display: 'flex',
      alignItems: 'center',
      width: '100%',
    }}
  >
    <SkuOptionRow option={option} />
  </Box>
);

const SkuSelectInput = <T extends FieldValues>({
  control,
  name,
  options,
  disabled = false,
  freeSolo = false,
  loading = false,
  required = false,
  hideLabel = false,
  placeholder,
  dataCy,
  hideErrorMsg = false,
}: SkuSelectInputProps<T>) => {
  const getInputDisplayValue = (storedValue: string, skuOptions: SkuOption[]) => {
    const match = skuOptions.find((sku) => sku.key === storedValue);
    return match ? getSkuDisplayLabel(match) : storedValue;
  };

  return (
    <AutocompleteInputBase
      control={control}
      name={name}
      options={options}
      multiple={false}
      placeholder={placeholder ?? (freeSolo ? 'Add Partner SKU' : 'Select a SKU')}
      freeSolo={freeSolo}
      selectedOnTop={!freeSolo}
      transformOption
      label={hideLabel ? 'SKU' : ''}
      required={required}
      hideLabel={hideLabel}
      hideOptional={!freeSolo}
      hideErrorMsg={hideErrorMsg}
      freeSoloSyncInput={freeSolo}
      normalizeFieldValue={freeSolo ? normalizeSkuFieldValue : undefined}
      getInputDisplayValue={freeSolo ? getInputDisplayValue : undefined}
      matchStoredValue={freeSolo ? (option, storedValue) => option.key === storedValue : undefined}
      getOptionLabel={
        freeSolo
          ? (option) => (typeof option === 'string' ? toInputString(option) : getSkuDisplayLabel(option))
          : undefined
      }
      filterOptionsWithContext={
        freeSolo
          ? (filterableOptions, state, context) => {
              if (context.showAllOptions) {
                return filterableOptions;
              }

              return filterSkuOptions(filterableOptions, {
                ...state,
                inputValue: toInputString(state.inputValue),
              });
            }
          : undefined
      }
      showAllOptionsOnOpen={freeSolo}
      startTypingText={loading ? 'Loading SKUs...' : 'No matching SKUs'}
      autocompleteProps={{
        disabled,
        ...(dataCy ? { 'data-cy': dataCy } : {}),
        ...(freeSolo
          ? {
              loading,
              forcePopupIcon: true,
              openOnFocus: true,
              sx: {
                width: '100%',
                '& .MuiInputBase-root': {
                  height: '40px',
                },
              },
              slotProps: {
                popper: {
                  placement: 'bottom-start',
                  sx: { zIndex: (theme: { zIndex: { modal: number } }) => theme.zIndex.modal + 1 },
                },
              },
            }
          : {
              sx: {
                backgroundColor: 'background.paper',
                '& .MuiInputBase-root': {
                  height: '40px',
                },
              },
            }),
      }}
      renderOption={renderSkuOption}
    />
  );
};

export default SkuSelectInput;
